import type {
  OrderTaskCargoItem,
  UpdateOrderTaskCargoItemRequest,
} from '@my/tms-api';
import { taskTypeOwnsCargoItems } from './cargoPick';
import type { CargoFamily, CargoItemDraft, MockTask } from './types';

// Both moved to `cargoPick.ts` so `taskTypeOwnsCargoItems` could use them
// without this module and that one importing each other. Re-exported here
// because this is where callers have always reached for them.
export { UNIT_IDENTIFIER_FAMILIES, unitIdentifierLabel } from './cargoPick';

/**
 * The unit draft whose fields belong on an allocation-derived cargo row.
 *
 * <p>
 * Delivery legs don't have a row per physical unit — they have a row per
 * COLLECTION SOURCE the operator drew from, with an `itemIndex` the
 * reconciler hands out in allocation order. So there is no stored link
 * between a delivery row and unit 1 vs unit 2, and the best available
 * answer is positional, matching what `cascadeTargets` does below: at one
 * unit every row belongs to it, at two the row's index picks the slot.
 * </p>
 *
 * <p>
 * Returns null when the index falls outside the drafts — a three-source
 * delivery on a two-unit variant has a third row that belongs to neither
 * slot, and guessing would put unit 1's barrel on someone else's cargo.
 * </p>
 */
export function unitDraftForRow(
  drafts: CargoItemDraft[],
  unitCount: number,
  itemIndex: number,
): CargoItemDraft | null {
  if (unitCount <= 1) return drafts[0] ?? null;
  return drafts[itemIndex - 1] ?? null;
}

/**
 * Slots whose identifier the operator has just entered or changed,
 * keyed by 1-based `itemIndex`.
 *
 * <p>
 * Cleared identifiers are deliberately excluded. Cascading a blank
 * would wipe downstream data on a confirm the operator might read as
 * "yes, copy the code" — and an emptied box is far more often a typo
 * being retyped than an instruction to strip the unit off the rest of
 * the job.
 * </p>
 */
export function changedUnitIdentifiers(
  drafts: CargoItemDraft[],
  initial: CargoItemDraft[],
): Map<number, string> {
  const out = new Map<number, string>();
  for (let i = 0; i < drafts.length; i++) {
    const draft = drafts[i];
    if (!draft) continue;
    const value = draft.identifier.trim();
    if (value === '') continue;
    if (value === (initial[i]?.identifier ?? '').trim()) continue;
    out.set(draft.itemIndex ?? i + 1, value);
  }
  return out;
}

/** One existing cargo row to stamp, with the row itself for the echo-back. */
export interface CascadeStamp {
  item: OrderTaskCargoItem;
  identifier: string;
}

/** A load-side slot the target task has no row for yet — needs a POST. */
export interface CascadeMissingSlot {
  itemIndex: number;
  identifier: string;
}

/** A following task the cascade would touch, and exactly what it would write. */
export interface CascadeTarget {
  task: MockTask;
  /** The BE task id — lifted out so callers don't re-assert `_source`. */
  taskId: string;
  stamps: CascadeStamp[];
  missingSlots: CascadeMissingSlot[];
}

/**
 * Tasks AFTER `fromSequence` that should carry the same unit
 * identifiers, and the rows on each that would change.
 *
 * <p>
 * Downstream only, by design: an earlier leg can legitimately have
 * carried a different box (a swap, a repositioning move), so reaching
 * backwards would overwrite history with the present.
 * </p>
 *
 * <p>
 * Unit matching depends on `unitCount`. At 1 the whole task belongs to
 * the single physical unit, so every cargo row on it is stamped —
 * including a delivery leg's allocation-derived rows, whose `itemIndex`
 * is assigned by the reconciler and carries no relation to a collection
 * slot. At 2 rows are matched by `itemIndex`, which is the only thing
 * tying unit 1 and unit 2 together across tasks.
 * </p>
 *
 * <p>
 * Missing rows are created on EVERY task type, delivery legs included.
 * A row made on a delivery has no `sourceCollectionItemId`, which makes
 * it a "legacy" row, and `reconcileDeliveryAllocations` deletes those
 * once the leg has allocations — so this used to skip delivery legs
 * rather than write something that would quietly disappear. The
 * reconciler now carries the unit fields off those rows onto the
 * allocation rows it creates before deleting them, so the identifier
 * survives the transition and the skip is no longer needed.
 * </p>
 *
 * <p>
 * That guard was also what made this behave as "every task type except
 * Unload and DropOff" — process events pass `cargoHandlingPhaseFor` as
 * load-side and got their row, the two delivery types did not.
 * </p>
 */
export function cascadeTargets({
  tasks,
  family,
  fromTaskId,
  fromSequence,
  unitCount,
  identifiers,
}: {
  tasks: MockTask[];
  /**
   * Decides which task types can receive the stamp. For container / tank
   * that is every type — a wash or an inspection names its barrel like
   * any other stop — so the cascade reaches the process events it used to
   * skip straight past.
   */
  family: CargoFamily;
  /** The task being saved — excluded even if it shares a sequence number. */
  fromTaskId: string;
  fromSequence: number;
  unitCount: number;
  identifiers: Map<number, string>;
}): CascadeTarget[] {
  if (identifiers.size === 0) return [];
  const out: CascadeTarget[] = [];

  for (const task of tasks) {
    const src = task._source;
    if (!src || src.id === fromTaskId) continue;
    if (src.sequence <= fromSequence) continue;
    if (src.state === 'Cancelled') continue;
    if (!taskTypeOwnsCargoItems(family, src.type)) continue;

    const rows = src.cargoItems ?? [];
    const stamps: CascadeStamp[] = [];
    const missingSlots: CascadeMissingSlot[] = [];
    // One row can only take one identifier. At unitCount 1 every row matches
    // every unit, so a task form showing MORE slots than the variant's unit
    // count (legacy rows expand beyond it) could otherwise stamp the same row
    // twice and silently leave unit 2's code on it. First unit wins.
    const claimed = new Set<string>();

    for (const [itemIndex, identifier] of identifiers) {
      const matches = (unitCount === 1 ? rows : rows.filter((r) => r.itemIndex === itemIndex)).filter(
        (r) => !claimed.has(r.id),
      );
      if (matches.length === 0) {
        // Nothing left to carry this unit. Only a genuinely empty slot earns a
        // new row — if the rows were all claimed by an earlier unit, creating
        // one at this `itemIndex` could collide with the unique index on
        // (OrderTaskId, ItemIndex).
        const slotIsFree = !rows.some((r) => r.itemIndex === itemIndex);
        if (slotIsFree) missingSlots.push({ itemIndex, identifier });
        continue;
      }
      for (const item of matches) {
        claimed.add(item.id);
        if ((item.identifier ?? '').trim() === identifier) continue;
        stamps.push({ item, identifier });
      }
    }

    if (stamps.length > 0 || missingSlots.length > 0) {
      out.push({ task, taskId: src.id, stamps, missingSlots });
    }
  }

  return out;
}

/**
 * Full-object PUT body for a cargo row, echoing every column the caller
 * isn't changing.
 *
 * <p>
 * `UpdateOrderTaskCargoItemHandler` assigns EVERY field from the
 * command, so an omitted property is not "leave it alone" — it nulls
 * the column. Anything that builds a body from a partial view of the
 * row (an allocation, a form's visible inputs, a cascade) has to
 * re-send the rest, and doing it here means one place to get right.
 * </p>
 */
export function cargoItemUpdateBody(
  item: OrderTaskCargoItem,
  patch: Partial<Omit<UpdateOrderTaskCargoItemRequest, 'cargoItemId'>> = {},
): UpdateOrderTaskCargoItemRequest {
  return {
    cargoItemId: item.id,
    itemIndex: item.itemIndex,
    sourceCollectionItemId: item.sourceCollectionItemId ?? null,
    description: item.description ?? null,
    quantity: item.quantity ?? null,
    quantityUnit: item.quantityUnit ?? null,
    weightKg: item.weightKg ?? null,
    volumeM3: item.volumeM3 ?? null,
    loadingMetresM: item.loadingMetresM ?? null,
    identifier: item.identifier ?? null,
    seal: item.seal ?? null,
    sealStatus: item.sealStatus ?? null,
    tankStatus: item.tankStatus ?? null,
    capacity: item.capacity ?? null,
    customsReference: item.customsReference ?? null,
    clearanceStatus: item.clearanceStatus ?? null,
    podStatus: item.podStatus ?? null,
    podSignedAt: item.podSignedAt ?? null,
    podSignedBy: item.podSignedBy ?? null,
    notes: item.notes ?? null,
    // Echoed with their ids, so each line keeps the copy it was given.
    sourceProductLineId: item.sourceProductLineId ?? null,
    products: (item.products ?? [])
      .slice()
      .sort((a, b) => a.sequence - b.sequence)
      .map((p) => ({ id: p.id, productId: p.productId, weightKg: p.weightKg, volumeM3: p.volumeM3 })),
    ...patch,
  };
}
